import React, { useCallback, useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { type Dayjs, formatDateSlashes } from '~/services/TimeService';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useHideExpiredRateGroups } from '../../shared/useHideExpiredRateGroups';
import { availabilityLabels, getAvailabilityChipColor } from '../surcharges/surchargeAvailability';
import { getUnmatchedCardSx } from '../surcharges/surchargeCardStyles';

import ManageRoomRateBlackoutDatesAction from './ManageRoomRateBlackoutDatesAction';
import {
  type RoomRateBlackoutAssignment,
  type RoomRateBlackoutGroup,
  groupRoomRateBlackoutAssignments,
} from './appliedBlackoutUtils';

interface Props {
  assignments: Array<RoomRateBlackoutAssignment>;
  hideExpired: boolean;
  now: Dayjs;
}

export default function UnmatchedBlackoutRoomRateCards({ assignments, hideExpired, now }: Props) {
  const roomGroups = useMemo(() => groupRoomRateBlackoutAssignments(assignments), [assignments]);

  const getBlackoutDates = useCallback((rateGroup: RoomRateBlackoutGroup) => rateGroup.blackoutDates, []);
  const isExpired = useCallback(
    (roomBlackoutDate: App.BlackoutDate) => getDateRangeAvailability(roomBlackoutDate, now) === 'expired',
    [now],
  );
  const visibleRateGroups = useHideExpiredRateGroups(roomGroups, getBlackoutDates, isExpired, hideExpired);

  if (assignments.length === 0) {
    return (
      <Box
        sx={(theme) => ({
          ...getUnmatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">No room-rate blackout blocks without a matching Salesforce blackout.</Typography>
      </Box>
    );
  }

  if (visibleRateGroups.every(({ visibleItems: visibleBlackoutDates }) => visibleBlackoutDates.length === 0)) {
    return (
      <Box
        sx={(theme) => ({
          ...getUnmatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">All unmatched room-rate blackout blocks are expired.</Typography>
      </Box>
    );
  }

  return (
    <>
      {visibleRateGroups.map(({ roomGroup, rateGroup, visibleItems: visibleBlackoutDates }) => {
        const ratePlanLabel = [rateGroup.ratePlanName, rateGroup.ratePlanCode].filter(Boolean).join(' - ');

        if (visibleBlackoutDates.length === 0) {
          return null;
        }

        return (
          <Box key={rateGroup.roomRateId} sx={getUnmatchedCardSx}>
            <Stack spacing={1.25}>
              <Stack
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <Typography fontWeight={700} variant="body2">
                    {roomGroup.roomName}
                  </Typography>
                  <ManageRoomRateBlackoutDatesAction href={rateGroup.href} />
                </Stack>
                <Typography
                  component={rateGroup.href ? 'a' : 'span'}
                  {...(rateGroup.href ? { href: rateGroup.href, target: '_blank', rel: 'noreferrer' } : {})}
                  color={rateGroup.href ? 'primary' : 'text.secondary'}
                  variant="body2"
                >
                  {ratePlanLabel}
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                {visibleBlackoutDates.map((roomBlackoutDate) => {
                  const availability = getDateRangeAvailability(roomBlackoutDate, now);

                  return (
                    <Stack
                      key={roomBlackoutDate.id}
                      alignItems={{ sm: 'center' }}
                      direction={{ sm: 'row' }}
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Typography variant="body2">
                        {formatDateSlashes(roomBlackoutDate.start_date)} to{' '}
                        {formatDateSlashes(roomBlackoutDate.end_date)} · {daysOfWeekMessage(roomBlackoutDate)}
                      </Typography>
                      <Chip
                        color={getAvailabilityChipColor(availability)}
                        label={availabilityLabels[availability]}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </>
  );
}
